/**
 * Price calculator
 *
 * Product-side pricing is derived from the same metafield tables the PDP discount table renders
 * (custom.pricing / custom.pricingB2B). Shipping is a real rate: a throwaway cart is created through the
 * Storefront API with the chosen address, and its delivery options are read back. The shopper's own cart is
 * never involved.
 *
 * Section: sections/price-calculator.liquid
 */
(function () {
  'use strict';

  var EM_DASH = '—';
  var RATE_POLL_ATTEMPTS = 4;
  var RATE_POLL_DELAY_MS = 600;
  var MIN_QUANTITY = 10;

  /**
   * Cells arrive as "12,50", "€ 1.234,56" or "9.75" depending on how the metafield table was filled in.
   * Decide the decimal separator from the string itself rather than assuming a locale.
   */
  function parseMoney(raw) {
    if (typeof raw === 'number') return isFinite(raw) ? raw : null;
    if (typeof raw !== 'string') return null;

    var cleaned = raw.replace(/[^\d.,-]/g, '').trim();
    if (cleaned === '' || cleaned === '-') return null;

    var lastComma = cleaned.lastIndexOf(',');
    var lastDot = cleaned.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
      // Whichever comes last is the decimal separator; the other groups thousands.
      cleaned = lastComma > lastDot
        ? cleaned.replace(/\./g, '').replace(',', '.')
        : cleaned.replace(/,/g, '');
    } else if (lastComma > -1) {
      // A lone comma is a decimal separator unless it is grouping (e.g. "1,234" with exactly 3 trailing digits).
      cleaned = /,\d{3}$/.test(cleaned) ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.');
    }

    var value = parseFloat(cleaned);
    return isFinite(value) ? value : null;
  }

  /** "10-49" | "500+" | "100" -> {min, max}. Returns null for anything that is not a quantity band. */
  function parseBand(raw) {
    if (typeof raw !== 'string') return null;

    var text = raw.replace(/\s/g, '');
    if (text === '') return null;

    var range = text.match(/^(\d+)[-–](\d+)$/);
    if (range) return { min: parseInt(range[1], 10), max: parseInt(range[2], 10) };

    var open = text.match(/^(\d+)\+$/);
    if (open) return { min: parseInt(open[1], 10), max: Infinity };

    var exact = text.match(/^(\d+)$/);
    if (exact) return { min: parseInt(exact[1], 10), max: parseInt(exact[1], 10) };

    return null;
  }

  function normalise(text) {
    return String(text == null ? '' : text).trim().toLowerCase();
  }

  /**
   * Rows come out of the metafield as objects keyed "1", "2", "3"... Object.keys puts integer-like keys in
   * ascending numeric order, so cell order is stable. Keys are kept because a band and its price share one.
   */
  function toRow(raw) {
    var keys = Array.isArray(raw)
      ? raw.map(function (_, index) { return String(index); })
      : Object.keys(raw || {});

    var cells = keys.map(function (key) {
      var value = Array.isArray(raw) ? raw[Number(key)] : raw[key];
      return { key: key, value: typeof value === 'string' ? value : String(value == null ? '' : value) };
    });

    // The label is the first cell holding non-numeric text ("Aantal", "Stickercase p/st", ...).
    var labelCell = cells.find(function (cell) {
      return cell.value.trim() !== '' && parseBand(cell.value) === null && parseMoney(cell.value) === null;
    });

    return { cells: cells, label: labelCell ? normalise(labelCell.value) : '' };
  }

  /** The band row is the one whose cells are quantity bands. Label match first, shape match as a fallback. */
  function findBandRow(rows) {
    var byLabel = rows.find(function (row) {
      return row.label === 'aantal' || row.label === 'number' || row.label === 'quantity';
    });
    if (byLabel) return byLabel;

    return rows.find(function (row) {
      var bands = row.cells.filter(function (cell) { return parseBand(cell.value) !== null; });
      return bands.length >= 2;
    }) || null;
  }

  /**
   * The price row is whichever row carries the base-price label from the product's own metafield. Falling back
   * to "the row after the bands" reproduces the layout these tables have always used, but only when that row
   * actually holds money.
   */
  function findPriceRow(rows, bandRow, baseLabel) {
    var wanted = normalise(baseLabel);

    if (wanted) {
      var byLabel = rows.find(function (row) { return row.label === wanted; });
      if (byLabel) return byLabel;
    }

    var next = rows[rows.indexOf(bandRow) + 1];
    if (next && next.cells.some(function (cell) { return parseMoney(cell.value) !== null; })) return next;

    return null;
  }

  /**
   * Returns {price, band, clamped} for a quantity, or null when the table cannot be read at all (the caller
   * then falls back to the variant price). A band and its price share a cell key, which is what pairs them.
   */
  function findTier(table, baseLabel, quantity) {
    if (!Array.isArray(table) || table.length === 0) return null;

    var rows = table.map(toRow);
    var bandRow = findBandRow(rows);
    if (!bandRow) return null;

    var priceRow = findPriceRow(rows, bandRow, baseLabel);
    if (!priceRow) return null;

    var tiers = bandRow.cells.reduce(function (all, cell) {
      var band = parseBand(cell.value);
      if (!band) return all;

      var priceCell = priceRow.cells.find(function (candidate) { return candidate.key === cell.key; });
      var price = priceCell ? parseMoney(priceCell.value) : null;
      if (price === null) return all;

      return all.concat({ band: band, label: cell.value.trim(), price: price });
    }, []);

    if (tiers.length === 0) return null;

    var exact = tiers.find(function (tier) {
      return quantity >= tier.band.min && quantity <= tier.band.max;
    });
    if (exact) return { price: exact.price, band: exact.label, clamped: false };

    /**
     * Outside the published bands the real price is genuinely unknown. Quote the nearest band and flag it,
     * rather than inventing a number: the old engine returned the last tier for any oversized order and the
     * variant price would be the single-unit rate, which is the most wrong answer available.
     */
    var lowest = tiers.reduce(function (a, b) { return b.band.min < a.band.min ? b : a; });
    if (quantity < lowest.band.min) return { price: lowest.price, band: lowest.label, clamped: true };

    var highest = tiers.reduce(function (a, b) { return b.band.min > a.band.min ? b : a; });
    return { price: highest.price, band: highest.label, clamped: true };
  }

  /**
   * withCarrierRates: true is what makes app / carrier-service rates (not just manually configured ones) show up,
   * and the Storefront API only allows it behind @defer — the delivery groups stream in once the carrier responds,
   * rather than blocking the whole cart response. The address lives on buyerIdentity.deliveryAddressPreferences.
   */
  var DELIVERY_FRAGMENT =
    ' ... on Cart @defer {' +
    '   deliveryGroups(first: 10, withCarrierRates: true) {' +
    '     nodes { deliveryOptions { handle title estimatedCost { amount currencyCode } } }' +
    '   }' +
    ' }';

  var CREATE_CART = 'mutation PriceCalculatorCart($input: CartInput!) {' +
    ' cartCreate(input: $input) {' +
    '   cart { id' + DELIVERY_FRAGMENT + ' }' +
    '   userErrors { field message }' +
    ' }' +
    '}';

  var READ_CART = 'query PriceCalculatorRates($id: ID!) { cart(id: $id) {' + DELIVERY_FRAGMENT + ' } }';

  /**
   * The delivery address needs the country's canonical ENGLISH name — a MailingAddressInput rejects ISO codes and
   * localized names alike ("Nederland" fails, "Netherlands" works). @inContext(language: EN) makes the API hand back
   * exactly the spelling it will accept, so the name always matches even for stores that run in another language.
   * Accept-Language would be simpler but browsers forbid setting it on fetch.
   */
  var COUNTRY_NAMES_QUERY =
    'query @inContext(language: EN) { localization { availableCountries { isoCode name } } }';

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /**
   * A @defer response is delivered as multipart/mixed: an initial JSON part, then one part per deferred chunk.
   * Split on the boundary and JSON-parse each part's body. Returns the list of parsed payloads.
   */
  function parseMultipart(text, contentType) {
    var boundaryMatch = contentType.match(/boundary=("?)([^";]+)\1/);
    var marker = '--' + (boundaryMatch ? boundaryMatch[2] : '-');

    return text.split(marker).reduce(function (payloads, segment) {
      var separator = segment.match(/\r?\n\r?\n/);
      if (!separator) return payloads;

      var body = segment.slice(separator.index + separator[0].length).trim();
      if (body === '' || body === '--') return payloads;

      try {
        payloads.push(JSON.parse(body));
      } catch (error) {
        // A truncated or non-JSON part (e.g. the trailing terminator) is not fatal; skip it.
      }
      return payloads;
    }, []);
  }

  /**
   * Deep-scan any parsed payload for delivery options. @defer delivers them in an incremental chunk whose exact
   * path shifts between API versions, so rather than reconstruct the path we gather every deliveryOptions array we
   * can find. Works identically for a plain (non-deferred) response.
   */
  function collectOptions(payload) {
    var found = [];

    (function walk(node) {
      if (!node || typeof node !== 'object') return;

      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }

      if (Array.isArray(node.deliveryOptions)) found = found.concat(node.deliveryOptions);

      Object.keys(node).forEach(function (key) { walk(node[key]); });
    })(payload);

    return found;
  }

  var PAIR_SEP = ' ';

  /** Colour is the last " - " segment of the title; used only when the colour_name metafield is blank. */
  function colorFromTitle(title) {
    var parts = String(title || '').split(' - ');
    return parts.length > 1 ? parts[parts.length - 1].trim() : String(title || '').trim();
  }

  /** Size is the "NN cm" token in the title; used only when the details_size metafield is blank. */
  function sizeFromTitle(title) {
    var match = String(title || '').match(/(\d+)\s*cm/i);
    return match ? match[1] + ' cm' : '';
  }

  /**
   * A locale-stable size key from the handle ("...-55-cm-zwart" -> "55"). The displayed label comes from the title,
   * but titles disagree between locales/products (a 55-handle reads "54 cm" in NL and "54"/"55 cm" in EN), so the
   * handle number is what actually groups sizes. Falls back to the title's number.
   */
  function sizeKeyFromHandle(handle, title) {
    var match = String(handle || '').match(/(\d+)-cm/i);
    if (match) return match[1];
    var fromTitle = String(title || '').match(/(\d+)\s*cm/i);
    return fromTitle ? fromTitle[1] : '';
  }

  /** Numeric value of a size label ("45 cm" -> 45) for sorting; non-numeric sizes sink to the end. */
  function sizeValue(label) {
    var match = String(label || '').match(/\d+/);
    return match ? parseInt(match[0], 10) : Number.MAX_SAFE_INTEGER;
  }

  /** Parse the "Name:#hex" per-line swatch setting into a lowercased colour-name -> hex map. */
  function parseSwatchMap(text) {
    var map = {};
    String(text || '').split('\n').forEach(function (line) {
      var index = line.indexOf(':');
      if (index === -1) return;
      var name = line.slice(0, index).trim().toLowerCase();
      var hex = line.slice(index + 1).trim();
      if (name && hex) map[name] = hex;
    });
    return map;
  }

  var PriceCalculator = class extends HTMLElement {
    connectedCallback() {
      var configElement = this.querySelector('[data-pc-config]');
      if (!configElement) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        return;
      }

      this.product = null;
      this.shipping = null;
      this.requestToken = 0;

      this.formatter = new Intl.NumberFormat(this.config.locale || undefined, {
        style: 'currency',
        currency: this.config.currency
      });

      this.quantityInput = this.querySelector('[data-pc-qty]');
      this.countrySelect = this.querySelector('[name="pc-country"]');
      this.zipInput = this.querySelector('[name="pc-zip"]');
      this.calculateButton = this.querySelector('[name="pc-calculate"]');
      this.personalizeInput = this.querySelector('[data-pc-personalize]');
      this.status = this.querySelector('[data-pc-status]');
      this.colorsContainer = this.querySelector('[data-pc-colors]');
      this.sizesContainer = this.querySelector('[data-pc-sizes]');
      this.stockEl = this.querySelector('[data-pc-stock]');

      this.selectedColor = null;
      this.selectedSize = null;
      this.colorHexMap = parseSwatchMap(this.config.colorSwatches);

      this.quantityInput.addEventListener('change', this._onQuantityChange.bind(this));
      this.quantityInput.addEventListener('input', this._onQuantityChange.bind(this));
      this.personalizeInput.addEventListener('change', this._render.bind(this));
      this.calculateButton.addEventListener('click', this._onCalculate.bind(this));

      this.querySelector('[data-pc-qty-minus]').addEventListener('click', this._step.bind(this, -1));
      this.querySelector('[data-pc-qty-plus]').addEventListener('click', this._step.bind(this, 1));

      // Address changes invalidate a rate that was fetched for a different destination.
      this.countrySelect.addEventListener('change', this._invalidateShipping.bind(this));
      this.zipInput.addEventListener('input', this._invalidateShipping.bind(this));

      this._buildModel();
      this._renderColors();
    }

    /**
     * Turn the flat product list into a colour x size grid. Each product is one colour+size; colour/size come from
     * the grouping metafields, falling back to the title. `matrix` keys a colour+size pair to its product.
     */
    _buildModel() {
      this.colors = [];
      this.sizes = [];
      this.matrix = {};
      this.sizesForColor = {};

      var colorSeen = {};
      var sizeSeen = {};
      var self = this;
      var hexMap = this.colorHexMap;

      (this.config.products || []).forEach(function (product) {
        var name = product.colorName || colorFromTitle(product.title);
        // Group case-insensitively: a messy catalogue can carry "Light Green" and "Light green" as two products.
        var colorKey = normalise(name);
        var sizeKey = sizeKeyFromHandle(product.handle, product.title);
        var sizeLabel = product.sizeLabel || sizeFromTitle(product.title) || (sizeKey && sizeKey + ' cm');
        if (!colorKey || !sizeKey) return;

        // Hex priority: a per-product metafield if it's ever populated, else the swatch-setting map by colour name.
        var hex = product.colorHex || hexMap[colorKey] || '';
        product._colorKey = colorKey;
        product._sizeKey = sizeKey;

        if (!colorSeen[colorKey]) {
          colorSeen[colorKey] = true;
          self.colors.push({ key: colorKey, name: name, hex: hex });
        } else if (hex) {
          var existing = self.colors.find(function (c) { return c.key === colorKey; });
          if (existing && !existing.hex) existing.hex = hex;
        }

        if (!sizeSeen[sizeKey]) {
          sizeSeen[sizeKey] = true;
          self.sizes.push({ key: sizeKey, label: sizeLabel });
        }

        // First product for a colour+size wins; duplicates in a messy catalogue collapse to one cell.
        var cell = colorKey + PAIR_SEP + sizeKey;
        if (!self.matrix[cell]) self.matrix[cell] = product;
        (self.sizesForColor[colorKey] = self.sizesForColor[colorKey] || {})[sizeKey] = true;
      });

      this.sizes.sort(function (a, b) { return sizeValue(a.key) - sizeValue(b.key); });
    }

    _renderColors() {
      var self = this;
      this.colorsContainer.textContent = '';

      this.colors.forEach(function (color) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'pc__swatch';
        button.title = color.name;
        button.setAttribute('aria-label', color.name);
        button.setAttribute('aria-pressed', 'false');
        button.dataset.colorKey = color.key;

        var dot = document.createElement('span');
        dot.className = 'pc__swatch-dot';
        dot.style.background = color.hex || 'transparent';
        if (!color.hex || color.hex.toUpperCase() === '#FFFFFF') dot.classList.add('pc__swatch-dot--bordered');
        button.appendChild(dot);

        button.addEventListener('click', function () { self._selectColor(color); });
        self.colorsContainer.appendChild(button);
      });
    }

    _selectColor(color) {
      this.selectedColor = color.key;

      Array.prototype.forEach.call(this.colorsContainer.children, function (button) {
        button.setAttribute('aria-pressed', String(button.dataset.colorKey === color.key));
      });
      this.querySelector('[data-pc-color-value]').textContent = ': ' + color.name;

      // Drop a size that this colour doesn't come in; keep it when it's still available.
      if (this.selectedSize && !(this.sizesForColor[color.key] && this.sizesForColor[color.key][this.selectedSize])) {
        this.selectedSize = null;
      }

      this._renderSizes();

      if (this.selectedSize) {
        this._applyProduct(this.matrix[color.key + PAIR_SEP + this.selectedSize]);
      } else {
        this._clearProduct();
      }
    }

    _renderSizes() {
      var self = this;
      var available = this.sizesForColor[this.selectedColor] || {};
      this.sizesContainer.textContent = '';

      this.sizes.forEach(function (size) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'pc__pill';
        button.textContent = size.label;
        button.dataset.sizeKey = size.key;
        var enabled = !!available[size.key];
        button.disabled = !enabled;
        button.setAttribute('aria-pressed', String(size.key === self.selectedSize));

        if (enabled) button.addEventListener('click', function () { self._selectSize(size.key, size.label); });
        self.sizesContainer.appendChild(button);
      });
    }

    _selectSize(key, label) {
      if (!this.selectedColor) return;

      this.selectedSize = key;
      Array.prototype.forEach.call(this.sizesContainer.children, function (button) {
        button.setAttribute('aria-pressed', String(button.dataset.sizeKey === key));
      });
      this.querySelector('[data-pc-size-value]').textContent = ': ' + label;

      this._applyProduct(this.matrix[this.selectedColor + PAIR_SEP + key]);
    }

    _clearProduct() {
      this.product = null;
      this.querySelector('[data-pc-size-value]').textContent = '';
      this.querySelector('[data-pc-preview]').hidden = true;
      this.querySelector('[data-pc-rows]').hidden = true;
      if (this.stockEl) this.stockEl.hidden = true;
      this._invalidateShipping();
    }

    /**
     * Mirrors the PDP's "46 Op voorraad" line. Once the requested quantity passes what's in stock, swap it for a
     * lead-time note — a price request for more than stock is a large/made-to-order job, not an out-of-stock error.
     */
    _renderStock() {
      if (!this.stockEl) return;

      if (!this.product || !this.product.inventoryManaged) {
        this.stockEl.hidden = true;
        return;
      }

      var inventory = Number(this.product.inventory) || 0;
      var overStock = this._quantity() > inventory;

      this.stockEl.classList.toggle('pc__stock--over', overStock);
      this.stockEl.textContent = overStock
        ? this._text('largeOrder', 'Mail ons op info@stickercase.nl voor de actuele levertijd bij grote bestellingen.')
        : inventory + ' ' + this._text('inStock', 'Op voorraad');
      this.stockEl.hidden = false;
    }

    _applyProduct(product) {
      if (!product) return this._clearProduct();

      this.product = product;
      this._invalidateShipping();

      this.querySelector('[data-pc-title]').textContent = product.title;

      var image = this.querySelector('[data-pc-preview-image]');
      var link = this.querySelector('[data-pc-preview-link]');
      if (product.image) {
        image.src = product.image;
        image.alt = product.title;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
      link.href = product.url;
      this.querySelector('[data-pc-preview]').hidden = false;

      // Snap up to the product's minimum, but never silently reduce a larger quantity the shopper chose.
      if (this._quantity() < this._minimum()) this.quantityInput.value = this._minimum();

      this.querySelector('[data-pc-rows]').hidden = false;
      this._applyLabels();
      this._onQuantityChange();
    }    

    _minimum() {
      return Math.max(MIN_QUANTITY, this.product && this.product.minQuantity > 0 ? this.product.minQuantity : 0);
    }

    _quantity() {
      var value = parseInt(this.quantityInput.value, 10);
      return isFinite(value) && value > 0 ? value : 0;
    }

    _applyLabels() {
      var labels = this.product.labels || {};

      if (labels.base) this.querySelector('[data-pc-label-base]').textContent = labels.base;

      var personalizeRow = this.querySelector('[data-pc-personalize-row]');
      personalizeRow.hidden = !(this.product.personalizeFee > 0);
      if (labels.personalize) this.querySelector('[data-pc-label-personalize]').textContent = labels.personalize;
      if (personalizeRow.hidden) this.personalizeInput.checked = false;

      var setupRow = this.querySelector('[data-pc-setup-row]');
      setupRow.hidden = !(this.product.setupFee > 0);
      if (labels.setup) this.querySelector('[data-pc-label-setup]').textContent = labels.setup;
    }

    _step(offset, event) {
      event.preventDefault();

      var next = Math.max(this._minimum(), this._quantity() + offset);
      this.quantityInput.value = next;
      this._onQuantityChange();
    }

    _onQuantityChange() {
      var minimum = this._minimum();
      var quantity = this._quantity();

      var hint = this.querySelector('[data-pc-min-hint]');
      var below = this.product && quantity > 0 && quantity < minimum;

      if (below) {
        hint.textContent = this._text('minHint', 'Minimaal %%COUNT%% stuks').replace('%%COUNT%%', minimum);
      }
      hint.hidden = !below;

      this.querySelector('[data-pc-qty-minus]').disabled = quantity <= minimum;
      this.querySelector('[data-pc-qty-echo]').textContent = quantity || 0;

      this._renderStock();
      this._invalidateShipping();
      this._render();
    }

    _invalidateShipping() {
      // Any input that feeds the rate call makes the previous rate wrong; drop it rather than show it as current.
      this.requestToken++;
      this.shipping = null;
      this._render();
    }

    _setStatus(message, isError) {
      this.status.textContent = message || '';
      this.status.classList.toggle('pc__status--error', Boolean(isError));
    }

    _onCalculate() {
      if (!this.product) {
        this._setStatus(this._text('chooseProduct', 'Kies eerst een product.'), true);
        return;
      }

      var quantity = this._quantity();
      if (quantity < this._minimum()) {
        this._setStatus(this._text('minQuantity', 'Vul een geldig aantal in.'), true);
        return;
      }

      if (!this.product.requiresShipping) {
        this.shipping = { amount: 0, title: '', options: [] };
        this._setStatus('');
        this._render();
        return;
      }

      if (!this.config.token) {
        this._setStatus(this._text('notConfigured', 'De verzendcalculator is nog niet geconfigureerd.'), true);
        return;
      }

      var zip = this.zipInput.value.trim();
      if (!zip) {
        this._setStatus(this._text('needZip', 'Vul een postcode in.'), true);
        return;
      }

      this._fetchRates(quantity, this.countrySelect.value, zip);
    }

    _text(key, fallback) {
      return (this.config.strings && this.config.strings[key]) || fallback;
    }

    /** ISO code -> canonical English country name, fetched once and cached for the element's lifetime. */
    _loadCountryNames() {      
      if (!this._countryNames) {
        this._countryNames = this._query(COUNTRY_NAMES_QUERY, {})
          .then(function (payloads) {
            console.log('[price-calculator] loaded country names', payloads);
            var localization = payloads[0] && payloads[0].data && payloads[0].data.localization;
            var countries = (localization && localization.availableCountries) || [];
            return countries.reduce(function (map, country) {
              map[country.isoCode] = country.name;
              return map;
            }, {});
          })
          .catch(function () {            
            return {}; // A miss falls through to the ISO code below, which surfaces a clear rate error.
          });
      }
      return this._countryNames;
    }

    /** Returns an array of parsed payloads: one for a plain response, several for a multipart @defer response. */
    async _query(query, variables) {
      // The Storefront API is same-origin on a live theme, so origin is the default. config.shopDomain overrides it
      // for previews served off the shop's own domain; the section never emits it in production.
      var origin = this.config.shopDomain ? 'https://' + this.config.shopDomain : window.location.origin;
      var endpoint = origin + '/api/' + this.config.apiVersion + '/graphql.json';

      var response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'multipart/mixed, application/json',
          'X-Shopify-Storefront-Access-Token': this.config.token
        },
        body: JSON.stringify({ query: query, variables: variables })
      });

      var contentType = response.headers.get('content-type') || '';
      var payloads = contentType.indexOf('multipart/mixed') !== -1
        ? parseMultipart(await response.text(), contentType)
        : [await response.json()];

      if (!response.ok) {
        var transportError = payloads.reduce(function (message, payload) {
          return message || (payload.errors && payload.errors[0] && payload.errors[0].message);
        }, null);
        throw new Error(transportError || 'Storefront API returned ' + response.status);
      }

      var errored = payloads.find(function (payload) { return payload.errors && payload.errors.length; });
      if (errored) throw new Error(errored.errors[0].message);

      return payloads;
    }

    async _fetchRates(quantity, countryCode, zip) {
      var token = ++this.requestToken;

      this.calculateButton.setAttribute('aria-busy', 'true');
      this._setStatus(this._text('calculating', 'Verzendkosten berekenen…'));

      try {
        var countryNames = await this._loadCountryNames();
        if (token !== this.requestToken) return;

        var payloads = await this._query(CREATE_CART, {
          input: {
            lines: [{ merchandiseId: this.product.variantGid, quantity: quantity }],
            buyerIdentity: {
              countryCode: countryCode,
              deliveryAddressPreferences: [{
                deliveryAddress: { country: countryNames[countryCode] || countryCode, zip: zip },
                deliveryAddressValidationStrategy: 'COUNTRY_CODE_ONLY'
              }]
            }
          }
        });

        var result = (payloads[0] && payloads[0].data && payloads[0].data.cartCreate) || {};
        if (result.userErrors && result.userErrors.length) throw new Error(result.userErrors[0].message);
        if (!result.cart) throw new Error('No cart returned');

        // The @defer stream already waited for carrier rates, but a plain-JSON version may answer before they
        // resolve; re-read a few times before concluding there are none.
        var options = collectOptions(payloads);
        for (var attempt = 0; attempt < RATE_POLL_ATTEMPTS && options.length === 0; attempt++) {
          await delay(RATE_POLL_DELAY_MS);
          if (token !== this.requestToken) return;

          options = collectOptions(await this._query(READ_CART, { id: result.cart.id }));
        }

        if (token !== this.requestToken) return;

        if (options.length === 0) {
          this.shipping = null;
          this._setStatus(this._text('noRates', 'Geen verzendopties gevonden voor dit adres.'), true);
          this._render();
          return;
        }

        var seen = {};
        var priced = options
          .map(function (option) {
            return {
              title: option.title,
              amount: parseFloat(option.estimatedCost && option.estimatedCost.amount) || 0,
              key: (option.handle || option.title) + ':' + (option.estimatedCost && option.estimatedCost.amount)
            };
          })
          .filter(function (option) {
            if (seen[option.key]) return false;
            seen[option.key] = true;
            return true;
          })
          .sort(function (a, b) { return a.amount - b.amount; });

        // The cheapest option drives the total; the rest are shown so the number is not a black box.
        this.shipping = { amount: priced[0].amount, title: priced[0].title, options: priced };
        this._setStatus('');
        this._render();
      } catch (error) {
        if (token !== this.requestToken) return;

        this.shipping = null;
        this._setStatus(this._text('rateError', 'Verzendkosten konden niet worden opgehaald.'), true);
        this._render();
      } finally {
        if (token === this.requestToken) this.calculateButton.removeAttribute('aria-busy');
      }
    }

    _money(value) {
      return this.formatter.format(value);
    }

    _render() {
      if (!this.product) return;

      var quantity = this._quantity();
      var valid = quantity >= this._minimum();

      var baseElement = this.querySelector('[data-pc-base]');
      var perItemElement = this.querySelector('[data-pc-per-item]');
      var lineElement = this.querySelector('[data-pc-line-total]');
      var setupElement = this.querySelector('[data-pc-setup]');
      var shippingElement = this.querySelector('[data-pc-shipping]');
      var totalElement = this.querySelector('[data-pc-total]');
      var personalizeElement = this.querySelector('[data-pc-personalize-fee]');

      var tier = valid ? findTier(this.product.pricing, this.product.labels && this.product.labels.base, quantity) : null;

      // With no readable tier table at all, the variant price is the only figure we have.
      var unitPrice = tier ? tier.price : this.product.basePriceCents / 100;

      this._renderTierNote(tier);

      var personalizeFee = this.personalizeInput.checked ? (this.product.personalizeFee || 0) : 0;
      var setupFee = this.product.setupFee || 0;

      baseElement.textContent = this._money(unitPrice);
      personalizeElement.textContent = this._money(personalizeFee);
      setupElement.textContent = this._money(setupFee);

      if (!valid) {
        perItemElement.textContent = EM_DASH;
        lineElement.textContent = EM_DASH;
        shippingElement.textContent = EM_DASH;
        totalElement.textContent = EM_DASH;
        this._renderShippingOptions();
        return;
      }

      var perItem = unitPrice + personalizeFee;
      var lineTotal = perItem * quantity;

      perItemElement.textContent = this._money(perItem);
      lineElement.textContent = this._money(lineTotal);

      if (this.shipping === null) {
        shippingElement.textContent = EM_DASH;
        totalElement.textContent = EM_DASH;
        this._renderShippingOptions();
        return;
      }

      shippingElement.textContent = this._money(this.shipping.amount);
      totalElement.textContent = this._money(lineTotal + setupFee + this.shipping.amount);

      this._renderShippingOptions();
    }

    /** Say so when the quantity sits outside the published bands, so the quoted tier is not read as exact. */
    _renderTierNote(tier) {
      var note = this.querySelector('[data-pc-tier-note]');
      if (!note) return;

      if (tier && tier.clamped) {
        note.textContent = this._text('tierClamped', 'Prijs op basis van staffel %%BAND%%. Vraag een offerte aan voor dit aantal.')
          .replace('%%BAND%%', tier.band);
        note.hidden = false;
      } else {
        note.hidden = true;
      }
    }

    /** Name the rate that drives the total, and list the alternatives so the number is not a black box. */
    _renderShippingOptions() {
      var element = this.querySelector('[data-pc-shipping-options]');
      if (!element) return;

      var options = (this.shipping && this.shipping.options) || [];
      if (options.length === 0) {
        element.hidden = true;
        return;
      }

      var self = this;
      element.textContent = options
        .map(function (option) { return option.title + ': ' + self._money(option.amount); })
        .join(' · ');
      element.hidden = false;
    }
  };

  if (!window.customElements.get('price-calculator')) {
    window.customElements.define('price-calculator', PriceCalculator);
  }
})();
